import { useEffect, useState } from "preact/hooks";
import {
  type Allowlist,
  type AllowlistEntry,
  getAllowlist,
  putEntry,
  removeEntry,
} from "./api.ts";
import { message } from "./errors.ts";
import { OverflowMenu } from "./menu.tsx";
import {
  Button,
  Callout,
  Card,
  Confirm,
  Field,
  Pill,
  Row,
  ScreenHead,
  ScreenTitle,
} from "./ui.tsx";
import { useAction } from "./use-action.ts";

interface Props {
  /** The signed-in admin, so the UI can say which row is theirs. */
  me: string;
  onBack: () => void;
}

export function AllowlistEditor({ me, onBack }: Props) {
  const [list, setList] = useState<Allowlist | null>(null);
  const [email, setEmail] = useState("");
  const [admin, setAdmin] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const { busy, error, setError, run } = useAction();

  useEffect(() => {
    getAllowlist().then(setList, (reason: unknown) => setError(message(reason)));
  }, [setError]);

  /** Every edit answers with the whole list, so nothing has to be re-fetched. */
  function edit(change: () => Promise<Allowlist>): Promise<boolean> {
    // The list on screen may be what a refusal was about, so reload it then.
    return run(
      async () => setList(await change()),
      () => getAllowlist().then(setList, () => undefined),
    );
  }

  function add(event: Event): void {
    event.preventDefault();
    const entry: AllowlistEntry = { email: email.trim(), admin };
    void edit(() => putEntry(entry)).then((ok) => {
      if (ok) {
        setEmail("");
        setAdmin(false);
      }
    });
  }

  return (
    <>
      <ScreenHead onBack={onBack} />
      <ScreenTitle>Who can sign in</ScreenTitle>
      <p class="lede">
        Everyone here can add and change the booth's messages. Admins can also change
        this list.
      </p>

      {error && <Callout tone="bad" title={error} onDismiss={() => setError(null)} />}
      {!list && !error && <p class="empty">Loading…</p>}

      {pending && (
        <Confirm
          title={`Remove ${pending}?`}
          body="They cannot sign in any more."
          confirmLabel="Remove"
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            // Closed whether or not it worked, the same as the delete confirm:
            // a refusal is a `Callout` behind this dialog's own backdrop.
            void edit(() => removeEntry(pending)).then(() => setPending(null));
          }}
        />
      )}

      {list && (
        <Card class="card-rows">
          {list.entries.map((entry) => (
            <Row
              key={entry.email}
              title={
                <>
                  {entry.email}
                  {entry.email === me && <span class="muted"> (you)</span>}
                </>
              }
              meta={entry.admin ? <Pill tone="brand" label="Admin" /> : undefined}
              trailing={
                <OverflowMenu
                  label={`More for ${entry.email}`}
                  items={[
                    {
                      label: entry.admin ? "Remove admin" : "Make an admin",
                      onSelect: () => {
                        void edit(() =>
                          putEntry({ email: entry.email, admin: !entry.admin }),
                        );
                      },
                    },
                    {
                      label: "Remove from the list",
                      destructive: true,
                      onSelect: () => setPending(entry.email),
                    },
                  ]}
                />
              }
            />
          ))}
        </Card>
      )}

      <section class="section">
        <h3 class="section-title">Add someone</h3>
        <Card>
          <form onSubmit={add}>
            <Field id="new-email" label="Email address">
              <input
                id="new-email"
                type="email"
                required
                value={email}
                disabled={busy}
                onInput={(event) => setEmail((event.target as HTMLInputElement).value)}
              />
            </Field>
            <label class="check-row">
              <input
                type="checkbox"
                checked={admin}
                disabled={busy}
                onChange={(event) => {
                  setAdmin((event.target as HTMLInputElement).checked);
                }}
              />
              <span>Can edit this list</span>
            </label>
            <Button
              tone="primary"
              type="submit"
              busy={busy}
              busyLabel="Adding…"
              disabled={email.trim() === ""}
            >
              Add
            </Button>
          </form>
        </Card>
      </section>
    </>
  );
}
